import { useCallback, useEffect, useRef, useState } from "react";
import { ApiError, request } from "./api";

export interface AccountUser { id: string; username: string }
export interface NavigationHistoryEntry {
  id: string;
  startedAt: number;
  destination: { id: string; name: string; address: string; lat: number; lng: number };
  origin: { label: string; lat: number; lng: number };
  distanceMeters: number;
  durationSeconds: number;
}
export interface AccountHistoryModel {
  user: AccountUser | null;
  ready: boolean;
  entries: NavigationHistoryEntry[];
  busy: boolean;
  error: string;
  register: (username: string, password: string) => Promise<boolean>;
  login: (username: string, password: string) => Promise<boolean>;
  logout: () => Promise<boolean>;
  deleteAccount: (password: string) => Promise<boolean>;
  clearHistory: () => Promise<boolean>;
  removeEntry: (id: string) => Promise<boolean>;
  recordNavigation: (entry: NavigationHistoryEntry) => Promise<boolean>;
  retry: () => Promise<boolean>;
}

const GUEST_KEY = "desty.guest.navigation-history.v1";
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
let guestMemory: NavigationHistoryEntry[] = [];
let guestStorageUnavailable = false;
function text(value: unknown, max: number): value is string {
  return typeof value === "string" && value.length > 0 && value.length <= max;
}
function coordinate(value: unknown, bound: number): value is number {
  return typeof value === "number" && Number.isFinite(value) && Math.abs(value) <= bound;
}
export function isHistoryEntry(value: unknown): value is NavigationHistoryEntry {
  if (!value || typeof value !== "object") return false;
  const entry = value as NavigationHistoryEntry;
  return typeof entry.id === "string" && UUID.test(entry.id) &&
    Number.isSafeInteger(entry.startedAt) && entry.startedAt > 0 && entry.startedAt <= Date.now() + 300_000 &&
    !!entry.destination && text(entry.destination.id, 200) && text(entry.destination.name, 200) &&
    typeof entry.destination.address === "string" && entry.destination.address.length <= 400 &&
    coordinate(entry.destination.lat, 90) && coordinate(entry.destination.lng, 180) &&
    !!entry.origin && text(entry.origin.label, 200) && coordinate(entry.origin.lat, 90) && coordinate(entry.origin.lng, 180) &&
    Number.isFinite(entry.distanceMeters) && entry.distanceMeters >= 0 && entry.distanceMeters <= 50_000_000 &&
    Number.isFinite(entry.durationSeconds) && entry.durationSeconds >= 0 && entry.durationSeconds <= 31_536_000;
}
function normalizeEntries(value: unknown, limit: number): NavigationHistoryEntry[] {
  if (!Array.isArray(value)) return [];
  const seen = new Set<string>();
  return value.filter(isHistoryEntry).sort((a, b) => b.startedAt - a.startedAt)
    .filter(entry => !seen.has(entry.id) && !!seen.add(entry.id)).slice(0, limit)
    .map(entry => ({ id: entry.id, startedAt: entry.startedAt,
      destination: { id: entry.destination.id, name: entry.destination.name, address: entry.destination.address,
        lat: entry.destination.lat, lng: entry.destination.lng },
      origin: { label: entry.origin.label, lat: entry.origin.lat, lng: entry.origin.lng },
      distanceMeters: entry.distanceMeters, durationSeconds: entry.durationSeconds }));
}
export function getGuestHistory(): NavigationHistoryEntry[] {
  if (!guestStorageUnavailable) {
    try {
      const raw = globalThis.localStorage?.getItem(GUEST_KEY);
      guestMemory = normalizeEntries(raw ? JSON.parse(raw) : [], 100);
    } catch { /* A blocked or corrupt store must not prevent navigation. */ }
  }
  return normalizeEntries(guestMemory, 100);
}
export function saveGuestHistory(entries: NavigationHistoryEntry[]): NavigationHistoryEntry[] {
  guestMemory = normalizeEntries(entries, 100);
  try {
    globalThis.localStorage.setItem(GUEST_KEY, JSON.stringify(guestMemory));
    guestStorageUnavailable = false;
  } catch { guestStorageUnavailable = true; }
  return normalizeEntries(guestMemory, 100);
}
function requireUser(value: unknown): AccountUser {
  const user = value as Partial<AccountUser> | null;
  if (!user || !text(user.id, 200) || !text(user.username, 32)) throw new Error("התקבלה תשובה לא תקינה מהשרת.");
  return { id: user.id, username: user.username };
}
function message(cause: unknown): string {
  if (cause instanceof ApiError && cause.status === 401) return "ההתחברות פגה או שהפרטים שגויים. התחברו שוב.";
  return cause instanceof Error ? cause.message : "לא הצלחנו להשלים את הפעולה. נסו שוב.";
}
const accountRequest = <T,>(path: string, init: RequestInit = {}) =>
  request<T>(`/api/account${path}`, { ...init, credentials: "include" });

/** Guest trips stay on this device. Signed-in trips are never cached locally. */
export function useAccountHistory(): AccountHistoryModel {
  const [user, setUser] = useState<AccountUser | null>(null);
  const [ready, setReady] = useState(false);
  const [entries, setEntries] = useState<NavigationHistoryEntry[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const mounted = useRef(false);
  const version = useRef(0);
  const identity = useRef<AccountUser | null | undefined>(undefined);
  const identityPending = useRef<Promise<boolean> | null>(null);
  const accountLock = useRef(false);
  const historyQueue = useRef<Promise<unknown>>(Promise.resolve());
  const valid = useCallback((generation: number) => mounted.current && generation === version.current, []);

  const fail = useCallback((cause: unknown, generation: number) => {
    if (!valid(generation)) return;
    if (cause instanceof ApiError && (cause.status === 401 || cause.status === 409)) {
      ++version.current;
      identity.current = undefined; accountLock.current = false;
      setUser(null); setReady(false); setEntries([]); setBusy(false);
    }
    setError(cause instanceof ApiError && cause.status === 409
      ? "החשבון השתנה בחלון אחר. רעננו את החשבון כדי להמשיך." : message(cause));
  }, [valid]);

  const beginTransition = useCallback(() => {
    const generation = ++version.current;
    identity.current = undefined;
    setUser(null); setEntries([]); setReady(false); setError(""); setBusy(true);
    return generation;
  }, []);

  const establish = useCallback(async (next: AccountUser | null, generation: number) => {
    if (!valid(generation)) return false;
    identity.current = next; setUser(next); setReady(true);
    if (!next) { setEntries(getGuestHistory()); return true; }
    const response = await accountRequest<{ entries: unknown }>("/history", { headers: { "X-Account-Id": next.id } });
    if (!valid(generation)) return false;
    if (!Array.isArray(response.entries)) throw new Error("לא הצלחנו לטעון את ההיסטוריה. נסו שוב.");
    setEntries(normalizeEntries(response.entries, 200));
    return true;
  }, [valid]);

  const retry = useCallback((): Promise<boolean> => {
    if (accountLock.current) return Promise.resolve(false);
    accountLock.current = true;
    const generation = beginTransition();
    const pending = (async () => {
      try {
        const result = await accountRequest<{ user: unknown }>("/session");
        return await establish(result.user === null ? null : requireUser(result.user), generation);
      } catch (cause) {
        fail(cause, generation);
        return false;
      } finally {
        if (generation === version.current) accountLock.current = false;
        if (valid(generation)) setBusy(false);
      }
    })();
    identityPending.current = pending;
    return pending;
  }, [beginTransition, establish, fail, valid]);

  useEffect(() => {
    mounted.current = true;
    // StrictMode cleanup invalidates the first request; allow the fresh setup.
    accountLock.current = false;
    void retry();
    return () => { mounted.current = false; ++version.current; identity.current = undefined; };
  }, [retry]);

  const changeAccount = useCallback((path: string, body?: object): Promise<boolean> => {
    if (accountLock.current) return Promise.resolve(false);
    const previousOwner = identity.current;
    const requiresOwner = path === "/logout" || path === "/delete";
    if (requiresOwner && !previousOwner) return Promise.resolve(false);
    accountLock.current = true;
    const generation = beginTransition();
    let authSucceeded = false;
    const pending = (async () => {
      try {
        const result = await accountRequest<{ user: unknown } | undefined>(path, { method: "POST",
          ...(requiresOwner && previousOwner ? { headers: { "X-Account-Id": previousOwner.id } } : {}),
          ...(body ? { body: JSON.stringify(body) } : {}) });
        authSucceeded = true;
        return await establish(requiresOwner ? null : requireUser(result?.user), generation);
      } catch (cause) {
        if (!valid(generation)) return false;
        if (cause instanceof ApiError && cause.status === 409 && (requiresOwner || authSucceeded)) { fail(cause, generation); return false; }
        // A failed login/logout never turns an existing server session into a guest.
        try {
          const current = await accountRequest<{ user: unknown }>("/session");
          await establish(current.user === null ? null : requireUser(current.user), generation);
        } catch (refreshFailure) {
          if (refreshFailure instanceof ApiError && (refreshFailure.status === 401 || refreshFailure.status === 409)) fail(refreshFailure, generation);
          // Otherwise leave identity unknown until an explicit retry succeeds.
        }
        if (valid(generation)) setError(message(cause));
        return false;
      } finally {
        if (generation === version.current) accountLock.current = false;
        if (valid(generation)) setBusy(false);
      }
    })();
    identityPending.current = pending;
    return pending;
  }, [beginTransition, establish, fail, valid]);

  const historyAction = useCallback((action: "record" | "remove" | "clear", payload?: NavigationHistoryEntry | string): Promise<boolean> => {
    const generation = version.current;
    const pendingIdentity = identityPending.current;
    const task = async () => {
      await pendingIdentity;
      if (!valid(generation) || identity.current === undefined) return false;
      const owner = identity.current;
      if (action !== "record") { setBusy(true); setError(""); }
      try {
        if (owner) {
          const path = action === "remove" ? `/history/${encodeURIComponent(payload as string)}/delete`
            : action === "clear" ? "/history/clear" : "/history";
          await accountRequest<unknown>(path, { method: "POST",
            headers: { "X-Account-Id": owner.id },
            ...(action === "record" ? { body: JSON.stringify(payload) } : {}) });
        }
        if (!valid(generation)) return false;
        const transform = (old: NavigationHistoryEntry[]) => action === "clear" ? [] : action === "remove"
          ? old.filter(entry => entry.id !== payload)
          : normalizeEntries([payload, ...old], owner ? 200 : 100);
        if (owner) setEntries(transform);
        else setEntries(saveGuestHistory(transform(getGuestHistory())));
        return true;
      } catch (cause) {
        fail(cause, generation);
        return false;
      } finally { if (valid(generation) && action !== "record") setBusy(false); }
    };
    const pending = historyQueue.current.then(task, task);
    historyQueue.current = pending;
    return pending;
  }, [fail, valid]);

  return { user, ready, entries, busy, error, retry,
    login: (username, password) => changeAccount("/login", { username: username.trim(), password }),
    register: (username, password) => changeAccount("/register", { username: username.trim(), password }),
    logout: () => changeAccount("/logout"),
    deleteAccount: password => changeAccount("/delete", { password }),
    clearHistory: () => historyAction("clear"),
    removeEntry: id => UUID.test(id) ? historyAction("remove", id) : Promise.resolve(false),
    recordNavigation: entry => isHistoryEntry(entry) ? historyAction("record", entry) : Promise.resolve(false),
  };
}
